import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineStack,
  Text,
} from "@shopify/polaris";
import { CircleChevronRightIcon, EditIcon } from "@shopify/polaris-icons";
import type { Game } from "../../api/campaigns";
import type { WheelCatalogTemplate } from "./wheelCatalogTemplates";
import { WheelGamePreview } from "./previews/WheelGamePreview";
import { DEFAULT_WHEEL_PREVIEW_COLORS, type WheelTemplatePreviewColors } from "../../types/wheel";

export interface WheelsOfFortuneFamilyCardProps {
  game: Game;
  statusBadge: ReactNode;
  templates: readonly WheelCatalogTemplate[];
  /** Backend-loaded preview colors per template id; empty object or missing keys fall back to code defaults. */
  previewColorsByTemplate?: Record<string, WheelTemplatePreviewColors> | null;
  onUseInCampaign: (templateId: string) => void;
}

/**
 * Single family card for the wheel engine: nested template rows with preview, copy, Edit, Use in Campaign.
 * Family name/description come from the surrounding GameCatalogSection.
 */
export function WheelsOfFortuneFamilyCard({
  game,
  statusBadge,
  templates,
  previewColorsByTemplate,
  onUseInCampaign,
}: WheelsOfFortuneFamilyCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const version = game.versions?.[0];

  return (
    <Card>
      <Box padding="500">
        <BlockStack gap="500">
          <InlineStack align="space-between" blockAlign="start" gap="400" wrap>
            <BlockStack gap="200">
              <InlineStack gap="300" blockAlign="center" wrap>
                <Text as="h3" variant="headingMd" fontWeight="semibold">
                  Wheel templates
                </Text>
                {statusBadge}
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Engine: {game.name}
                {version ? ` · v${version.version}` : ""}
                {version?.placements?.length
                  ? ` · Placements: ${version.placements.join(", ")}`
                  : ""}
              </Text>
            </BlockStack>
          </InlineStack>

          {game.tags && game.tags.length > 0 && (
            <InlineStack gap="200" wrap>
              {game.tags.map((tag) => (
                <Badge key={tag}>{tag}</Badge>
              ))}
            </InlineStack>
          )}

          <Divider />

          <BlockStack gap="400">
            {templates.map((template) => {
              const c =
                previewColorsByTemplate?.[template.id] ?? DEFAULT_WHEEL_PREVIEW_COLORS;
              return (
                <Box
                  key={template.id}
                  padding="400"
                  background="bg-surface-secondary"
                  borderRadius="200"
                >
                  <InlineStack
                    align="space-between"
                    blockAlign="start"
                    gap="400"
                    wrap
                  >
                    <Box minWidth="120px" maxWidth="168px">
                      <WheelGamePreview
                        segmentFills={c.segmentHexes}
                        pointerFill={c.pointerHex}
                        rimStroke={c.rimStrokeHex}
                        outerRingFill={c.outerRingHex}
                      />
                      <Text
                        as="p"
                        variant="bodySm"
                        tone="subdued"
                        alignment="center"
                      >
                        {template.previewCaption}
                      </Text>
                    </Box>

                    <Box minWidth="200px" maxWidth="480px">
                      <BlockStack gap="150">
                        <Text variant="headingSm" as="h3">
                          {template.title}
                        </Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {template.description}
                        </Text>
                      </BlockStack>
                    </Box>

                    <BlockStack gap="200">
                      <Button
                        icon={EditIcon}
                        onClick={() =>
                          navigate({
                            pathname: `/games/wheel-templates/${template.id}`,
                            search: location.search,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant="primary"
                        icon={CircleChevronRightIcon}
                        onClick={() => onUseInCampaign(template.id)}
                      >
                        Use in Campaign
                      </Button>
                    </BlockStack>
                  </InlineStack>
                </Box>
              );
            })}
          </BlockStack>
        </BlockStack>
      </Box>
    </Card>
  );
}
